import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Button,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import DataTable from '@/components/DataTable/DataTable';
import { PaymentRailSettingsApi } from '@/api/clients/payment-rail-settings/payment-rail-settings.api';
import { useConfirmation } from '@/context/ConfirmationContext';
import PaymentRailSettingFormDialog from './PaymentRailSettingFormDialog';

const PaymentRailSettings = () => {
  const { t } = useTranslation();
  const dataTableRef = useRef(null);
  const { requestConfirmation } = useConfirmation();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const refresh = () => dataTableRef.current?.refresh();

  const handleCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleEdit = (id) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleDelete = (id, name) => {
    requestConfirmation(
      t('paymentRailSettings.confirmDelete', { name }),
      async () => {
        setLoading(true);
        try {
          await PaymentRailSettingsApi.delete(id);
          refresh();
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const columnFilters = useMemo(
    () => [
      {
        key: 'search',
        label: t('paymentRailSettings.search'),
        filterType: 'search',
        placeholder: t('paymentRailSettings.searchPlaceholder'),
        debounce: true,
        debounceDelay: 300,
      },
    ],
    [t],
  );

  const columns = [
    {
      key: 'name',
      label: t('paymentRailSettings.table.name'),
      sortable: true,
    },
    {
      key: 'paymentRailSettingIdentifiers',
      label: t('paymentRailSettings.table.identifiers'),
      render: (value) => {
        if (!value || value.length === 0) return '-';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {value.slice(0, 3).map((identifier, index) => (
              <Chip key={index} label={identifier} size='small' />
            ))}
            {value.length > 3 && (
              <Chip
                label={`+${value.length - 3}`}
                size='small'
                variant='outlined'
              />
            )}
          </Box>
        );
      },
    },
    {
      key: 'isDisabled',
      label: t('paymentRailSettings.table.status'),
      render: (value) => (
        <Chip
          size='small'
          label={
            value
              ? t('paymentRailSettings.status.disabled')
              : t('paymentRailSettings.status.enabled')
          }
          color={value ? 'default' : 'success'}
          variant={value ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      key: 'actions',
      label: t('paymentRailSettings.table.actions'),
      sortable: false,
      align: 'right',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title={t('paymentRailSettings.editTooltip')}>
            <IconButton
              size='small'
              color='primary'
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.id);
              }}
              disabled={loading}>
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('paymentRailSettings.deleteTooltip')}>
            <IconButton
              size='small'
              color='error'
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.id, row.name);
              }}
              disabled={loading}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant='h5' sx={{ mb: 2 }}>
        {t('paymentRailSettings.title')}
      </Typography>

      <DataTable
        ref={dataTableRef}
        columns={columns}
        endpoint='/payment-rail-settings'
        initialSort={{ key: 'name', order: 'asc' }}
        pageSize={100}
        autoFetch
        enableColumnPicker
        enableSorting
        enableFilters
        columnFilters={columnFilters}
        externalLoading={loading}
        renderToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button
              variant='outlined'
              color='primary'
              startIcon={<AddIcon />}
              onClick={handleCreate}>
              {t('paymentRailSettings.createButton')}
            </Button>
          </Box>
        )}
        renderEmptyState={() => (
          <TableRow>
            <TableCell colSpan={columns.length} align='center' sx={{ py: 8 }}>
              <Typography variant='body1' color='text.secondary'>
                {t('paymentRailSettings.noData')}
              </Typography>
            </TableCell>
          </TableRow>
        )}
      />

      <PaymentRailSettingFormDialog
        open={dialogOpen}
        settingId={editingId}
        onClose={handleCloseDialog}
        onSaved={refresh}
      />
    </Box>
  );
};

export default PaymentRailSettings;
